import React, { useState, useEffect } from "react";
import Header from "./Header";
import TextInsertion from "./TextInsertion";
import ChatPage from "./Chat";
import MotDePasseOublierPage from "./MotDePasseOublier";
import InscriptionPage from "./Inscription";
import ProfilPage from "./Profil";
import ConnexionPage from "./Connexion";
import InformationManipulationPage from "./Standardisation";
import GeneralAssistant from "./AssistantGeneral";
import FullMenu from "./FullMenu";
import Credit from "./Credit/CreditPage";
import Accueil from "./Accueil";
import Home from "./Home";
import GuideLivre from "./GuideLivre";
import {
  insertText,
  respondToCasePrompt,
  createAccount,
  sendPasswordRecoveryEmail,
  verifyRecoveryCode,
  manipulateData,
  seConnecter,
  askAndExecuteGeneralRequest,
  sendVerificationEmail,
  getAccountData,
  logOut,
  handlePayment,
  askBookQuestion,
} from "../taskpane";
import { ArrowLeftRegular } from "@fluentui/react-icons";
import {
  Button,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  Switch,
  makeStyles,
  tokens,
  FluentProvider,
  webLightTheme,
  webDarkTheme,
} from "@fluentui/react-components";

/**
 * Interface définissant les propriétés du composant App.
 */
interface AppProps {
  title: string;
}

/**
 * Composant principal de l'application.
 * Gère la navigation entre les différentes pages et les états globaux.
 */
const App: React.FC<AppProps> = (props: AppProps) => {
  const styles = useStyles();

  // États globaux
  const [currentView, setCurrentView] = useState<string>("Accueil"); // Vue actuelle
  const [isDarkMode, setIsDarkMode] = useState(false); // Mode sombre
  const [loading, setLoading] = useState(true); // Indicateur de chargement
  const [isLoggedIn, setIsLoggedIn] = useState(false); // État de connexion
  const [reloadMenu, setReloadMenu] = useState(false); // Indicateur de rechargement du menu

  /**
   * Fonction appelée en cas de succès d'un abonnement.
   * Redirige l'utilisateur vers la page de profil.
   */
  const handleSubscriptionSuccess = () => {
    setCurrentView("Profil");
  };

  useEffect(() => {
    setReloadMenu((prev) => !prev);
  }, [currentView]);

  /**
   * Vérifie si l'utilisateur est authentifié au chargement de l'application.
   */
  useEffect(() => {
    const checkUserAuthentication = async () => {
      try {
        const userData = await getAccountData();
        if (userData) {
          setIsLoggedIn(true);
          setCurrentView("Home");
        } else {
          setIsLoggedIn(false);
          setCurrentView("Accueil");
        }
      } catch (error) {
        console.error("Error fetching account data:", error);
        setCurrentView("Accueil");
      } finally {
        setLoading(false);
      }
    };

    checkUserAuthentication();
  }, []);

  /**
   * Écoute les messages de paiement (succès ou échec) provenant d'autres fenêtres.
   */
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === "payment_success") {
        handleSubscriptionSuccess();
      } else if (event.data && event.data.type === "payment_failed") {
        alert("Le paiement a échoué. Veuillez réessayer.");
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  /**
   * Active ou désactive le mode sombre.
   */
  const handleDarkModeChange = () => {
    setIsDarkMode((prevMode) => {
      return !prevMode;
    });
  };

  /**
   * Déconnecte l'utilisateur et redirige vers la page d'accueil.
   */
  const handleLogOut = async () => {
    try {
      await logOut();
      setIsLoggedIn(false);
      setCurrentView("Accueil");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  /**
   * Gère la connexion de l'utilisateur.
   * @param data - Données de connexion (email et mot de passe).
   */
  const handleLogIn = async (data: {
    email: string;
    password: string;
  }): Promise<{ message: string }> => {
    try {
      await seConnecter(data);
      setIsLoggedIn(true);
      setCurrentView("Home");
      return { message: "Login successful" };
    } catch (error: any) {
      console.error("Login failed: ", error);
      throw new Error(error.message || "An unexpected error occurred");
    }
  };

  /**
   * Gère le paiement d'un abonnement.
   * @param type - Type d'abonnement.
   */
  const handlePaymentWrapper = async (type: string) => {
    await handlePayment(type);
  };

  /**
   * Rend la vue actuelle en fonction de l'état `currentView`.
   */
  const renderView = () => {
    switch (currentView) {
      case "Home":
        return <Home setCurrentView={setCurrentView} />;
      case "Accueil":
        return <Accueil setCurrentView={setCurrentView} />;
      case "ChatPage": // Générateur de formules
        return (
          <>
            <ChatPage
              respondToCasePrompt={respondToCasePrompt}
              handleMenuReload={() => setReloadMenu(!reloadMenu)}
            />
          </>
        );
      case "MotDePasseOublier":
        return (
          <>
            <MotDePasseOublierPage
              setCurrentView={setCurrentView}
              sendPasswordRecoveryEmail={sendPasswordRecoveryEmail}
              verifyRecoveryCode={verifyRecoveryCode}
              changingPassword={false}
            />
            <Button className={styles.back} onClick={() => setCurrentView("Accueil")}>
              <ArrowLeftRegular />
            </Button>
          </>
        );
      case "ChangerDeMotDePasse":
        return (
          <>
            <MotDePasseOublierPage
              setCurrentView={setCurrentView}
              sendPasswordRecoveryEmail={sendPasswordRecoveryEmail}
              verifyRecoveryCode={verifyRecoveryCode}
              changingPassword={true}
            />
            <Button className={styles.back} onClick={() => setCurrentView("Home")}>
              <ArrowLeftRegular />
            </Button>
          </>
        );
      case "Connexion":
        return (
          <>
            <ConnexionPage setCurrentView={setCurrentView} seConnecter={handleLogIn} />
            <Button className={styles.back} onClick={() => setCurrentView("Accueil")}>
              <ArrowLeftRegular />
            </Button>
          </>
        );
      case "Profil":
        return (
          <>
            <ProfilPage
              setCurrentView={setCurrentView}
              handleMenuReload={() => setReloadMenu(!reloadMenu)}
            />
          </>
        );
      case "SignupPage":
        return (
          <>
            <InscriptionPage
              setCurrentView={setCurrentView}
              createAccount={createAccount}
              verifyEmail={sendVerificationEmail}
            />
            <Button className={styles.back} onClick={() => setCurrentView("Accueil")}>
              <ArrowLeftRegular />
            </Button>
          </>
        );
      case "StandardisationPage":
        return (
          <>
            <InformationManipulationPage
              standardiseOrExtractData={manipulateData}
              calledFunction="standardise"
              handleMenuReload={() => setReloadMenu(!reloadMenu)}
            />
          </>
        );
      case "ExtractionPage":
        return (
          <>
            <InformationManipulationPage
              standardiseOrExtractData={manipulateData}
              calledFunction="extract"
              handleMenuReload={() => setReloadMenu(!reloadMenu)}
            />
          </>
        );
      case "GeneralAssistant":
        return (
          <>
            <GeneralAssistant
              askAndExecuteGeneralRequest={askAndExecuteGeneralRequest}
              handleMenuReload={() => setReloadMenu(!reloadMenu)}
            />
          </>
        );
      case "CreditPage":
        return (
          <>
            <Credit handlePayment={handlePaymentWrapper} />
          </>
        );

      case "GuideLivre":
        return (
          <>
            <GuideLivre
              handleMenuReload={() => setReloadMenu(!reloadMenu)}
              setCurrentView={setCurrentView}
              askBookQuestion={askBookQuestion}
            />
          </>
        );
      default:
        return <div>View not found</div>;
    }
  };

  return (
    // Gestion du thème sombre
    <FluentProvider theme={isDarkMode ? webDarkTheme : webLightTheme}>
      <div className={styles.root}>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <>
            {isLoggedIn && (
              <FullMenu
                isDarkMode={isDarkMode}
                handleDarkModeChange={handleDarkModeChange}
                setCurrentView={setCurrentView}
                logoutFunc={handleLogOut}
                isLoggedIn={isLoggedIn}
                reload={reloadMenu}
              />
            )}
            {currentView !== "Home" && (
              <Header logo="assets/img_logo_agrandi.png" title={props.title} message="Welcome" />
            )}
            {renderView()}
          </>
        )}
      </div>
    </FluentProvider>
  );
};

export default App;

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    minWidth: "400px",
    backgroundColor: tokens.colorNeutralBackground3,
  },
  fieldFlexColumn: {
    display: "flex",
    flexDirection: "column",
  },
  fieldAlignLeft: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  buttonNonInteractive: {
    pointerEvents: "none",
  },
  container: {
    padding: "20px",
  },
  text: {
    fontSize: "18px",
    marginBottom: "10px",
  },
  fieldPadding: {
    marginBottom: "15px",
  },
  back: {
    position: "absolute",
    top: "10px",
    marginLeft: "20px",
    minWidth: "0px",
    fontSize: "20px",
    padding: "10px",
  },
});
